import { AfterContentInit, AfterViewInit, Component, ContentChild, EventEmitter, Input, OnInit, Output, signal, TemplateRef, ViewChild, } from '@angular/core';
import { MatCell, MatCellDef, MatColumnDef, MatFooterCellDef, MatFooterRow, MatFooterRowDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatNoDataRow, MatRow, MatRowDef, MatTable, MatTableDataSource, } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader, SortDirection } from '@angular/material/sort';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AbstractComponent } from '../abstract-component';
import { ResponsiveConfig } from '../../../models/responsive-config';
import { takeUntil } from 'rxjs/operators';
import { responsiveBreakpoints } from '../../utils/const/responsive-breakpoints';
import { LoaderService } from '../../services/loader.service';
import { Observable } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe, DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { NoResultComponent } from '../no-result/no-result.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { MatCheckboxModule } from '@angular/material/checkbox';

export interface ColumnDefinition {
  name: string;
  header?: string;
  sortable?: boolean;
  cellType?: string;
  isCellCustomized?: boolean;
  headerStyle?: string;
  headerIcon?: string;
  headerSvgIcon?: string;
  cellStyle?: string;
  isEditable?: boolean;
  checkbox?: boolean;
}

@Component({
  selector: 'logpn-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  imports: [
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatIcon,
    MatCellDef,
    MatCell,
    NgTemplateOutlet,
    MatFooterCellDef,
    MatPaginator,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgClass,
    MatNoDataRow,
    NoResultComponent,
    MatFooterRowDef,
    MatFooterRow,
    AsyncPipe,
    DatePipe,
    TranslocoPipe,
    MatCheckboxModule,
  ],
})
export class TableComponent
  extends AbstractComponent
  implements OnInit, AfterViewInit, AfterContentInit
{
  private _responsiveDisplayedColumns?: ResponsiveConfig<string[]>;

  @Input() set responsiveDisplayedColumns(value: ResponsiveConfig<string[]> | undefined) {
    this._responsiveDisplayedColumns = value;
    this.updateDisplayedColumns();
  }

  get responsiveDisplayedColumns(): ResponsiveConfig<string[]> | undefined {
    return this._responsiveDisplayedColumns;
  }

  @Output() clickRowEvent = new EventEmitter<any>();
  isClickableRow: boolean = false;
  @Input() displayPaginator: boolean = true;

  private _displayedColumns!: string[];
  @Input() set displayedColumns(displayedColumns: string[]) {
    this._displayedColumns = displayedColumns;
  }

  get displayedColumns(): string[] {
    return this._displayedColumns;
  }

  @Input() columnsDefinition!: ColumnDefinition[];

  @Input() defaultSort?: string;
  @Input() defaultSortDirection?: SortDirection;

  private _dataSource!: MatTableDataSource<any>;
  @Input() set dataSource(dataSource: MatTableDataSource<any>) {
    this._dataSource = dataSource;
  }

  get dataSource(): MatTableDataSource<any> {
    return this._dataSource;
  }

  @Input() selectedRow?: any;

  // ContentChild for custom cells template
  @ContentChild('customCells', { static: false })
  customCellsTemplateRef!: TemplateRef<any>;

  // ContentChild for action template
  @ContentChild('actions', { static: false })
  actionsTemplateRef!: TemplateRef<any>;

  // ContentChild for footer template
  @ContentChild('footer', { static: false })
  footerTemplateRef!: TemplateRef<any>;

  @ViewChild(MatTable) matTable?: MatTable<any>;

  // ViewChild for pagination and sorting
  private paginator!: MatPaginator;

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.dataSource.paginator = this.paginator;
  }

  private sort!: MatSort;

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.dataSource.sort = this.sort;
  }

  private readonly _checkedRows = signal<number[]>([]);
  @Input() set checkedRows(checkedRows: number[]) {
    this._checkedRows.set(checkedRows);
  }
  @Output() checkedRowsUpdated = new EventEmitter<number[]>();
  allRowsChecked = signal<boolean>(false);

  protected loading$!: Observable<boolean>;

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    private readonly loaderService: LoaderService,
  ) {
    super();
  }

  ngOnInit() {
    const breakpoints = [
      responsiveBreakpoints.small,
      responsiveBreakpoints.medium,
      responsiveBreakpoints.large,
      responsiveBreakpoints.extraLarge,
    ].filter((breakpoint): breakpoint is string => !!breakpoint);

    this.breakpointObserver
      .observe(breakpoints)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (!this.responsiveDisplayedColumns) {
          return;
        }

        if (
          state.breakpoints[responsiveBreakpoints.extraLarge!] &&
          this.responsiveDisplayedColumns?.extraLarge
        ) {
          this.displayedColumns = this.responsiveDisplayedColumns.extraLarge!;
        } else if (state.breakpoints[responsiveBreakpoints.large]) {
          this.displayedColumns = this.responsiveDisplayedColumns?.large;
        } else if (state.breakpoints[responsiveBreakpoints.medium]) {
          this.displayedColumns = this.responsiveDisplayedColumns?.medium;
        } else if (state.breakpoints[responsiveBreakpoints.small]) {
          this.displayedColumns = this.responsiveDisplayedColumns?.small;
        }
      });
    this.checkClickableRow();

    this.loading$ = this.loaderService.loaderIsVisible;
  }

  private updateDisplayedColumns() {
    if (!this.responsiveDisplayedColumns) return;

    if (
      responsiveBreakpoints.extraLarge &&
      this.breakpointObserver.isMatched(responsiveBreakpoints.extraLarge) &&
      this.responsiveDisplayedColumns?.extraLarge
    ) {
      this.displayedColumns = this.responsiveDisplayedColumns.extraLarge!;
    } else if (this.breakpointObserver.isMatched(responsiveBreakpoints.large)) {
      this.displayedColumns = this.responsiveDisplayedColumns.large;
    } else if (this.breakpointObserver.isMatched(responsiveBreakpoints.medium)) {
      this.displayedColumns = this.responsiveDisplayedColumns.medium;
    } else if (this.breakpointObserver.isMatched(responsiveBreakpoints.small)) {
      this.displayedColumns = this.responsiveDisplayedColumns.small;
    }
  }

  ngAfterViewInit() {
    this.matTable?.renderRows();
  }

  ngAfterContentInit() {
    if (this.actionsTemplateRef && !this.displayedColumns.includes('actions')) {
      this.displayedColumns.push('actions');
      this.matTable?.renderRows();
    }
  }

  checkClickableRow(): void {
    this.isClickableRow = this.clickRowEvent.observed;
  }

  //reset paginator to first page
  public firstPage() {
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  emitRowData(row: any) {
    this.clickRowEvent.emit(row);
  }

  onKeyDownHandler(event: KeyboardEvent, index: number) {
    if (event.key === 'Enter') {
      this.emitRowData(index);
    }
  }

  checkedAllRowsChanged(checked: boolean): void {
    this._checkedRows.set(checked ? this._dataSource.filteredData.map((item) => item.id) : []);
    this.allRowsChecked.set(checked);

    this.checkedRowsUpdated.emit(this._checkedRows());
  }

  checkedRowChanged(itemId: number, checked: boolean): void {
    if (checked) {
      this._checkedRows.update((rows) => [...rows, itemId]);
    } else {
      this._checkedRows.update((rows) => rows.filter((checkedId) => checkedId !== itemId));
    }

    this.allRowsChecked.set(this._checkedRows().length === this._dataSource.filteredData.length);

    this.checkedRowsUpdated.emit(this._checkedRows());
  }

  rowChecked(itemId: number): boolean {
    return this._checkedRows().includes(itemId);
  }
}
